import { useEffect, useState } from 'react';
import { Card, Empty, Page, PhotoPicker, Row, Spinner, api, brl, dateTime, useAuth } from '@mobi/web';

interface Tx {
  id: number;
  amountCents: number;
  kind: string;
  label: string;
  rideId: number | null;
  description: string;
  createdAt: string;
}

const ICON: Record<string, JSX.Element> = {
  promo: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
      <path d="M12 2 9.2 7.6 3 8.5l4.5 4.4L6.4 19 12 16.1 17.6 19l-1.1-6.1L21 8.5l-6.2-.9L12 2Z" />
    </svg>
  ),
  refund: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
      <path d="M13 3a9 9 0 1 0 8.5 12h-2.2A6.8 6.8 0 1 1 13 5.2V9l5-4.5L13 0v3Z" />
    </svg>
  ),
  ride_debit: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
      <path d="M5 16.5V18a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-6l2-5.2A2 2 0 0 1 5.9 5.5h12.2a2 2 0 0 1 1.9 1.3L22 12v6a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-1.5H5Zm.6-9L4.4 11h15.2l-1.2-3.5H5.6ZM6.5 15a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm11 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
    </svg>
  ),
  adjustment: (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
      <path d="M3 17v2h6v-2H3ZM3 5v2h10V5H3Zm10 16v-2h8v-2h-8v-2h-2v6h2ZM7 9v2H3v2h4v2h2V9H7Zm14 4v-2H11v2h10Zm-6-4h2V7h4V5h-4V3h-2v6Z" />
    </svg>
  ),
};

export function Wallet() {
  const { user } = useAuth();
  const [data, setData] = useState<{ balanceCents: number; transactions: Tx[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ balanceCents: number; transactions: Tx[] }>('/api/wallet')
      .then(setData)
      .catch(() => setError('não foi possível carregar sua carteira'));
  }, []);

  return (
    <Page title="Carteira">
      {user && (
        <div className="mb-6">
          <PhotoPicker name={user.name} hint="O motorista te reconhece no embarque." />
        </div>
      )}

      <Card tone="brand">
        <p className="eyebrow opacity-70">Saldo disponível</p>
        <p className="display mt-2 text-5xl leading-none">
          {data ? brl(data.balanceCents) : '—'}
        </p>
        <p className="mt-4 text-sm leading-relaxed opacity-80">
          Abatido automaticamente do valor da próxima corrida. Não é sacável.
        </p>
      </Card>

      <div className="mt-8 flex items-baseline justify-between">
        <h2 className="text-lg font-semibold">Extrato</h2>
        {data && data.transactions.length > 0 && (
          <span className="text-sm text-faint">
            {data.transactions.length} lançamento{data.transactions.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {error && <p className="mt-4 text-sm text-danger">{error}</p>}

      {!data && !error && (
        <div className="mt-10 grid place-items-center text-brand">
          <Spinner className="h-7 w-7" />
        </div>
      )}

      {data && data.transactions.length === 0 && (
        <div className="mt-4">
          <Empty
            title="Nenhum lançamento ainda"
            hint="Estornos e créditos promocionais aparecem aqui."
            icon={ICON.promo}
          />
        </div>
      )}

      {data && data.transactions.length > 0 && (
        <ul className="mt-2 divide-y divide-line">
          {data.transactions.map((tx) => (
            <li key={tx.id}>
              <Row
                icon={ICON[tx.kind] ?? ICON.adjustment}
                title={tx.label}
                subtitle={tx.description}
                meta={dateTime(tx.createdAt)}
                valueTone={tx.amountCents > 0 ? 'good' : 'muted'}
                value={`${tx.amountCents > 0 ? '+' : '−'}${brl(Math.abs(tx.amountCents))}`}
              />
            </li>
          ))}
        </ul>
      )}
    </Page>
  );
}
